import { useEffect, useRef, useState } from 'react';
import { reaction } from 'mobx';
import { first } from 'rxjs/operators';
import { requestChartData } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/charts/requestChartData';
import { buildMarketDataKey } from '../../../../../../../stores/websocketStore/utils/marketDataRouting';
import { groupByFunction } from '../../../../../../../stores/websocketStore/utils/rxjs/groupByFunction';
import { addSymbol } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/symbols/addSymbol';
import { removeSymbol } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/symbols/removeSymbol';
import { buildSymbolSubscription, sameSymbolSubscription } from '../../../../../../../stores/websocketStore/utils/symbolSubscriptionLifecycle';
import style from './ChartContainer.module.css';
import { min, max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { zoom, select, zoomTransform } from 'd3';
import { useWebsocketStore } from '../../../../../../../stores/websocketStore/websocketProvider';
import { MouseCoordinatesWrapper } from '../MouseCoordinatesWrapper';
import VerticalTicks from '../VerticalTicks';
import HorizontalTicks from '../HorizontalTicks';
import { OrdersContainer } from '../orders/OrdersContainer';
import ChartCrossHairs from '../ChartCrossHairs';
import SoftOrdersContainer from '../softOrders/SoftOrdersContainer';
import VerticalLabels from '../VerticalLabels';
import HorizontalLabels from '../HorizontalLabels';
import { CurrentCandle } from '../CurrentCandle';
import { OldCandles } from '../../OldCandles';
import { GapMarkers } from '../GapMarkers';
import { calculateMillisecondsBetweenCandles } from '../utils/calculateMillisecondsBetweenCandles';
import { buildChartTimeline } from '../utils/buildChartTimeline';
import { useContextMenu } from 'react-contexify';
import { ChartContextMenu } from '../chartContextMenu/ChartContextMenu';
import { observer } from 'mobx-react-lite';
import { colors } from '../../../../../../../colorVars';
import { DisplayPreTactics } from '../displayPreTactics/DisplayPreTactics';
import { buildChartState, normalizeGetChartResponse } from './chartResponseUtils';
import { ChartSkeleton } from './ChartSkeleton';
import {
  computeNeedsSessionWindows,
  isSessionWindowsReady,
} from '../utils/sessionWindowsReadiness';

const bottomPadding = 15;
const SESSION_WINDOWS_GRACE_MS = 3000;
const CHART_REQUEST_TIMEOUT_MS = 30000;
interface Props {
  autoSend?: boolean;
  tradePlanId: string;
  symbol: string;
  dataSource: string;
  brokerage: string;
  accountNumber?: string;
  numberOfCandles: number;
  timeUnit: string;
  candleDuration: number;
  id: string | number;
  size: number;
  margin: number;
  showCandleNumbers: boolean;
  startHour: string | number;
  startMin: string | number;
}
export const RefactoredChartContainer: React.FC<Props> = observer(
  ({
    autoSend,
    tradePlanId,
    symbol,
    dataSource,
    brokerage,
    accountNumber,
    numberOfCandles,
    timeUnit,
    candleDuration,
    id,
    size,
    margin,
    showCandleNumbers,
    startHour,
    startMin,
  }: Props) => {
    const { chartSubject, connected, chartCandleStore } = useWebsocketStore();
    const normalizedSymbol = symbol?.trim?.().toUpperCase?.() ?? symbol;
    const msgId =
      normalizedSymbol +
      candleDuration +
      timeUnit +
      numberOfCandles +
      dataSource +
      brokerage +
      id;
    const hash = buildMarketDataKey({
      symbol: normalizedSymbol,
      dataSource,
      brokerage,
    });
    const { show } = useContextMenu({ id: tradePlanId });
    // Rendering on the futures-minute anchor-fallback grid and then re-rendering once
    // sessionWindows arrives is what caused the whole visible chart to reflow a few moments after
    // load/duration-switch — see sessionWindowsReadiness.js for the full rationale, and its tests
    // for the equities/futures/duration-1 distinctions this depends on.
    const needsSessionWindows = computeNeedsSessionWindows(
      normalizedSymbol,
      timeUnit,
      candleDuration
    );
    const [sessionWindowsGraceExpired, setSessionWindowsGraceExpired] = useState(false);
    // Mirrors sessionWindowsGraceExpired for the mobx `reaction` below, whose callback closure is
    // fixed at effect-setup time and won't see later React state updates — the ref lets it read
    // the live value without re-subscribing the reaction on every grace-timer tick.
    const sessionWindowsGraceExpiredRef = useRef(false);
    useEffect(() => {
      sessionWindowsGraceExpiredRef.current = sessionWindowsGraceExpired;
    }, [sessionWindowsGraceExpired]);

    //STATE
    const [oldCandles, setOldCandles] = useState(null);
    const [currentCandle, setCurrentCandle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [priceMinimum, setPriceMinimum] = useState(0);
    const [priceMaximum, setPriceMaximum] = useState(0);
    const [scaleX, setScaleX] = useState(null);
    const [scaleY, setScaleY] = useState(null);
    const [currentZoomState, setCurrentZoomState] = useState(null);
    const [currentYZoomState, setCurrentYZoomState] = useState(null);
    const [timeStampArray, setTimeStampArray] = useState(null);
    const [caliber, setCaliber] = useState(null);
    const [millisBetweenCandles, setMillisBetweenCandles] = useState(null);
    const [realNumberOfCandles, setRealNumberOfCandles] = useState(30);
    const [gaps, setGaps] = useState([]);
    const [sessionWindows, setSessionWindows] = useState([]);
    // True once the initial getChart response has come back (even with zero candles), so the
    // empty-state placeholder below can distinguish "still waiting on the first response" from
    // "backend genuinely has nothing yet, but ChartCandleStore may still fill in via the
    // stream/trade path" (PRD Functional Requirement #5).
    const [initialResponseReceived, setInitialResponseReceived] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);

    //REFS
    const zoomRef = useRef(null);
    const verticalZoomRef = useRef(null);
    const portalRef = useRef();
    const activeSubscriptionRef = useRef(null);
    const watchlistNameRef = useRef(
      `${
        tradePlanId && tradePlanId !== 'none'
          ? `__tradePlanChart${tradePlanId}`
          : `__chart${String(id)}`
      }_${Math.random().toString(36).slice(2, 10)}`
    );

    //FUNCTIONS
    function updateOldCandles({ closedCandle, nextCandle }) {
      // PRD Functional Requirement #6: ChartCandleStore is authoritative for any bucket it
      // already has. If the store already owns the bucket being closed, the store-driven
      // history reaction below has already (or will already) render it — appending it here too
      // would double-add the same candle to local state.
      if (
        closedCandle &&
        chartCandleStore?.hasCandle({
          symbol: normalizedSymbol,
          dataSource,
          brokerage,
          timeUnit,
          candleDuration,
          timeStamp: closedCandle.timeStamp,
        })
      ) {
        return;
      }

      const actualCandles = [...(oldCandles ?? []), closedCandle, nextCandle]
        .slice(-numberOfCandles);
      const timeline = buildChartTimeline({
        candles: actualCandles,
        millisBetweenCandles,
      });
      const timelineCandles = [...timeline.candles];
      const rebuiltCurrentCandle = timelineCandles.pop();

      setPriceMinimum(min(timeline.candles, (c) => c.low));
      setPriceMaximum(max(timeline.candles, (c) => c.high));
      setOldCandles(timelineCandles);
      setCurrentCandle(rebuiltCurrentCandle);
      setTimeStampArray(timeline.timeStampArray);
      setRealNumberOfCandles(timeline.totalSlots);
      setGaps(timeline.gaps);
    }

    function applyChartState(chartState) {
      if (!chartState) {
        return;
      }
      setRealNumberOfCandles(chartState.realNumberOfCandles);
      setPriceMinimum(chartState.priceMinimum);
      setPriceMaximum(chartState.priceMaximum);
      setTimeStampArray(chartState.timeStampArray);
      setCaliber(chartState.caliber);
      setGaps(chartState.gaps);
      setSessionWindows(chartState.sessionWindows ?? []);
      setOldCandles(chartState.oldCandles);
      setCurrentCandle(chartState.currentCandle);
    }

    const displayContextMenu = (e, y) => {
      // e.preventDefault();
      show(e, { props: { yCoordinate: y } });
    };

    useEffect(() => {
      if (autoSend === false) {
        activeSubscriptionRef.current = null;
        return undefined;
      }

      const subscription = buildSymbolSubscription({
        symbol,
        dataSource,
        brokerage,
        watchlist_name: watchlistNameRef.current,
      });

      if (!connected) {
        activeSubscriptionRef.current = subscription;
        return undefined;
      }

      if (!subscription) {
        activeSubscriptionRef.current = null;
        return undefined;
      }

      const previousSubscription = activeSubscriptionRef.current;
      const isReplay = sameSymbolSubscription(previousSubscription, subscription);

      if (!isReplay && previousSubscription) {
        removeSymbol(previousSubscription);
      }

      addSymbol(subscription);
      activeSubscriptionRef.current = subscription;

      return () => {
        if (connected && activeSubscriptionRef.current) {
          removeSymbol(activeSubscriptionRef.current);
        }
      };
    }, [autoSend, connected, symbol, dataSource, brokerage]);

    // REQUESTS CHART DATA AND SEEDS ChartCandleStore. RUNS ON LOAD AND WHENEVER CHART PARAMETERS
    // CHANGE. The getChart response only seeds the store now — it does not build local
    // oldCandles/currentCandle state directly, and it never retries. Rendering is entirely
    // driven by the history reaction below, which is the single source of truth for
    // oldCandles/currentCandle once the store has anything for this route (PRD Functional
    // Requirements #2 and #5; see also specs/candle-streaming.md, "Remove the retry loop
    // entirely").
    useEffect(() => {
      if (!connected || !millisBetweenCandles) {
        return undefined;
      }
      setLoading(true);
      setInitialResponseReceived(false);
      setRequestError(null);
      // A new route must not inherit the previous route's rendered candles or Y-axis while
      // its response is pending. This is especially important for valid-but-empty routes
      // (for example an unsupported or unmapped futures root), where the store reaction has
      // nothing to apply and must leave the chart in an explicit empty state.
      setOldCandles(null);
      setCurrentCandle(null);
      setPriceMinimum(0);
      setPriceMaximum(0);
      setScaleX(null);
      setScaleY(null);
      setTimeStampArray(null);
      setGaps([]);
      if (!normalizedSymbol) {
        // The symbol field updates the chart while it is being edited. Keep the empty/editing
        // state local instead of asking the provider for a blank symbol.
        setLoading(false);
        setInitialResponseReceived(true);
        return undefined;
      }
      let cancelled = false;
      let requestTimeoutId: ReturnType<typeof setTimeout> | undefined;
      const chartObservable = groupByFunction('msgId', msgId, chartSubject).pipe(
        first()
      );
      const chartSubscription = chartObservable.subscribe((msg) => {
        if (cancelled) {
          return;
        }
        const { normalizedCandles, normalizedSessionWindows } =
          normalizeGetChartResponse({
            msg,
            timeUnit,
            candleDuration,
            numberOfCandles,
          });

        chartCandleStore.seedFromGetChart({
          symbol: normalizedSymbol,
          dataSource,
          brokerage,
          timeUnit,
          candleDuration,
          candles: normalizedCandles,
          sessionWindows: normalizedSessionWindows,
        });
        clearTimeout(requestTimeoutId);
        setInitialResponseReceived(true);
      });
      const requestSent = requestChartData({
        msgId,
        symbol: normalizedSymbol,
        dataSource,
        candleDuration,
        numberOfCandles,
        timeUnit,
        brokerage,
        accountNumber,
      });
      if (!requestSent) {
        chartSubscription.unsubscribe();
        setLoading(false);
        setRequestError(
          dataSource === 'LIVE' && !brokerage
            ? 'Select a brokerage to load this chart.'
            : 'This chart does not have a valid market-data route.'
        );
      } else {
        requestTimeoutId = setTimeout(() => {
          if (!cancelled) {
            chartSubscription.unsubscribe();
            setLoading(false);
            setRequestError('Chart data did not respond. Try changing the timeframe to retry.');
          }
        }, CHART_REQUEST_TIMEOUT_MS);
      }
      return () => {
        cancelled = true;
        clearTimeout(requestTimeoutId);
        chartSubscription.unsubscribe();
      };
    }, [
      symbol,
      dataSource,
      brokerage,
      accountNumber,
      chartSubject,
      chartCandleStore,
      numberOfCandles,
      timeUnit,
      candleDuration,
      connected,
      msgId,
      millisBetweenCandles,
      normalizedSymbol,
    ]);

    // REGISTERS THIS ROUTE WITH ChartCandleStore AND RENDERS FROM ITS HISTORY. Registration
    // (separate from the getChart effect above) tells the store which symbol+timeUnit+duration
    // combinations are actually in view, so a bare trade/timesale tick — which carries no
    // timeUnit/duration of its own — knows which bucket size(s) to derive a candle into (PRD
    // Functional Requirement #4). The reaction re-renders on every store update for this route,
    // including trade-derived updates for sparse Tradier symbols, and fires immediately on
    // mount if another already-mounted chart already populated this route's history.
    useEffect(() => {
      if (!chartCandleStore || !millisBetweenCandles) {
        return undefined;
      }

      const routeKey = chartCandleStore.registerRoute({
        symbol: normalizedSymbol,
        dataSource,
        brokerage,
        timeUnit,
        candleDuration,
      });

      const disposeReaction = reaction(
        () =>
          chartCandleStore.getHistory({
            symbol: normalizedSymbol,
            dataSource,
            brokerage,
            timeUnit,
            candleDuration,
            limit: numberOfCandles,
          }),
        (candles) => {
          if (!candles || candles.length === 0) {
            return;
          }
          const currentSessionWindows = chartCandleStore.getSessionWindows({
            symbol: normalizedSymbol,
            dataSource,
            brokerage,
            timeUnit,
            candleDuration,
          });
          applyChartState(
            buildChartState({
              candles,
              sessionWindows: currentSessionWindows,
              millisBetweenCandles,
              size,
              margin,
            })
          );
          // Only drop the skeleton here once the store's history is actually the FULL
          // requested set (e.g. another already-mounted chart on this same route already has
          // the complete seeded history). A handful of live/trade-derived candles trickling in
          // on a not-yet-seeded route is not "ready" — clearing loading on candles.length >= 1
          // was what caused the skeleton to flash and then reveal a sparse, broken-looking
          // chart. The genuinely-partial-response case (backend has fewer than numberOfCandles
          // total, e.g. a brand-new symbol) is instead handled by the initialResponseReceived
          // effect below, which clears loading once this component's own request is answered.
          // For futures MINUTES>1 charts, also hold the skeleton until sessionWindows is
          // populated (or the grace period above expires) so we never render (then reflow) an
          // anchor-fallback grid for longer than necessary.
          if (
            candles.length >= numberOfCandles &&
            isSessionWindowsReady({
              needsSessionWindows,
              sessionWindows: currentSessionWindows,
              graceExpired: sessionWindowsGraceExpiredRef.current,
            })
          ) {
            setLoading(false);
          }
        },
        { fireImmediately: true }
      );

      return () => {
        disposeReaction();
        if (routeKey) {
          chartCandleStore.unregisterRoute(routeKey);
        }
      };
    }, [
      chartCandleStore,
      normalizedSymbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
      numberOfCandles,
      millisBetweenCandles,
      size,
      margin,
    ]);

    // RELEASES THE LOADING STATE ONCE THIS COMPONENT'S OWN REQUEST HAS BEEN ANSWERED. The
    // history reaction above only clears loading early when the store already has the FULL
    // requested candle count (e.g. seeded by another already-mounted chart on the same route).
    // Otherwise we wait here: once our own getChart response has come back and been used to
    // seed the store (even with fewer than numberOfCandles, e.g. a brand-new symbol with a
    // short trading history, or genuinely zero candles), that's the authoritative "this route
    // has whatever data exists" signal, so the skeleton/empty-state can safely take over from
    // the loading skeleton (PRD Functional Requirement #5: "Keep loading only while initial
    // request is outstanding"). If the store fills in more afterward via a later stream/trade
    // event, the reaction above continues to re-render on every store update regardless.
    useEffect(() => {
      if (!initialResponseReceived) {
        return;
      }
      const currentSessionWindows = chartCandleStore.getSessionWindows({
        symbol: normalizedSymbol,
        dataSource,
        brokerage,
        timeUnit,
        candleDuration,
      });
      if (
        !isSessionWindowsReady({
          needsSessionWindows,
          sessionWindows: currentSessionWindows,
          graceExpired: sessionWindowsGraceExpired,
        })
      ) {
        return;
      }
      setLoading(false);
    }, [
      initialResponseReceived,
      needsSessionWindows,
      sessionWindowsGraceExpired,
      chartCandleStore,
      normalizedSymbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    ]);

    // GRACE-PERIOD TIMEOUT for the sessionWindows wait above and in the history reaction. Starts
    // once our own getChart response is in hand; if sessionWindows still hasn't shown up after
    // SESSION_WINDOWS_GRACE_MS, we stop waiting and let the chart render on whatever anchor is
    // available rather than hold the skeleton forever (see needsSessionWindows comment above).
    useEffect(() => {
      setSessionWindowsGraceExpired(false);
      if (!needsSessionWindows || !initialResponseReceived) {
        return undefined;
      }
      const timeoutId = setTimeout(() => {
        setSessionWindowsGraceExpired(true);
      }, SESSION_WINDOWS_GRACE_MS);
      return () => clearTimeout(timeoutId);
    }, [
      needsSessionWindows,
      initialResponseReceived,
      normalizedSymbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    ]);

    //SETS MILLISECONDS BETWEEN CANDLES
    useEffect(() => {
      const millis = calculateMillisecondsBetweenCandles({
        timeUnit,
        candleDuration,
      });
      setMillisBetweenCandles(millis);
    }, [timeUnit, candleDuration]);

    //SETS SCALES, RUNS AFTER CHART DATA IS RECEIVED, WHEN MIN/MAX CHANGES AND WHEN ZOOM CHANGES
    useEffect(() => {
      const xScale = scaleLinear()
        .domain([0, realNumberOfCandles])
        .range([0, size])
        .nice();
      if (currentZoomState !== null) {
        const newScale = currentZoomState.rescaleX(xScale);
        xScale.domain(newScale.domain());
        setScaleX(() => xScale);
        setCaliber(
          Math.max(
            (size / realNumberOfCandles - margin / currentZoomState.k) *
              currentZoomState.k,
            1
          )
        );
      } else {
        setScaleX(() => xScale);
        setCaliber(Math.max(size / realNumberOfCandles - margin, 1));
      }

      const yScale = scaleLinear()
        .domain([priceMinimum, priceMaximum])
        .range([size, 0])
        .nice();
      if (currentYZoomState !== null) {
        const newScale = currentYZoomState.rescaleY(yScale);
        yScale.domain(newScale.domain());
        setScaleY(() => yScale);
      } else setScaleY(() => yScale);
    }, [
      oldCandles,
      priceMinimum,
      priceMaximum,
      currentZoomState,
      currentYZoomState,
      size,
    ]);

    //SETS ZOOM STATE WHEN USER ZOOMS
    useEffect(() => {
      const svg = select(zoomRef.current);
      const zoomBehavior = zoom()
        .filter((event) => event.altKey)
        .scaleExtent([1, 10])
        .translateExtent([
          [0, 0],
          [size, size],
        ])
        .on('zoom', () => {
          setCurrentZoomState(zoomTransform(svg.node()));
        });
      zoomBehavior(svg);
    }, [size]);

    useEffect(() => {
      const svg = select(verticalZoomRef.current);
      const zoomBehavior = zoom()
        .filter((event) => event.altKey)
        .scaleExtent([0.1, 2])
        .translateExtent([
          [0, 0],
          [size, size],
        ])
        .on('zoom', () => {
          setCurrentYZoomState(zoomTransform(svg.node()));
        });
      zoomBehavior(svg);
    }, [size]);

    // PRD UX Requirement: "Never show a blank chart when at least one valid candle exists" —
    // and the inverse: when there genuinely are zero candles after the initial response, show
    // an explicit placeholder instead of an unexplained blank SVG. Loading already covers the
    // "still waiting on the first response" case; this only renders once loading has ended.
    const showEmptyState =
      !loading && !requestError && !currentCandle && !(oldCandles?.length > 0);
    // While the initial request is outstanding, the store may already hold partial/in-progress
    // candle data for this route (e.g. from another mounted chart streaming ticks). Rendering
    // that partial data mid-load looks broken, so show a skeleton instead until loading clears.
    const showSkeleton = loading;

    return (
      <div className={style.outerContainer}>
        <div
          style={{
            display: 'flex',
            width: 'fit-content',
          }}
        >
          {showSkeleton ? (
            <ChartSkeleton size={size} bottomPadding={bottomPadding} />
          ) : null}
          {!showSkeleton && requestError ? (
            <div
              className={style.emptyChartState}
              style={{ width: size, height: size + bottomPadding }}
            >
              {requestError}
            </div>
          ) : null}
          {!showSkeleton && showEmptyState ? (
            <div
              className={style.emptyChartState}
              style={{ width: size, height: size + bottomPadding }}
            >
              No candles yet for {symbol}.
            </div>
          ) : null}
          {!showSkeleton && !showEmptyState && !requestError ? (
          <MouseCoordinatesWrapper
            tradePlanId={tradePlanId}
            displayContextMenu={displayContextMenu}
            svgRef={zoomRef}
          >
            <svg
              width={size}
              height={size + bottomPadding}
              ref={zoomRef}
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <line
                x1={0}
                x2={0}
                y1={0}
                y2={size + bottomPadding}
                stroke={colors.lightGrey}
              />
              <line x1={0} x2={size} y1={0} y2={0} stroke={colors.lightGrey} />

              {currentCandle ? (
                <CurrentCandle
                  currentCandle={currentCandle}
                  caliber={caliber}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  symbol={symbol}
                  hash={hash}
                  brokerage={brokerage}
                  loading={loading}
                  currentZoomState={currentZoomState}
                  portalRef={portalRef}
                  size={size}
                  sessionWindows={sessionWindows}
                  secondToLast={oldCandles[oldCandles.length - 1]}
                  numberOfCandles={realNumberOfCandles}
                  updateOldCandles={updateOldCandles}
                  millisBetweenCandles={millisBetweenCandles}
                  priceMinimum={priceMinimum}
                  priceMaximum={priceMaximum}
                  setPriceMinimum={setPriceMinimum}
                  setPriceMaximum={setPriceMaximum}
                  dataSource={dataSource}
                  showCandleNumbers={showCandleNumbers}
                  startHour={startHour}
                  startMin={startMin}
                  timeUnit={timeUnit}
                  candleDuration={candleDuration}
                />
              ) : null}
              {oldCandles ? (
                <OldCandles
                  oldCandles={oldCandles}
                  caliber={caliber}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  loading={loading}
                  currentZoomState={currentZoomState}
                  portalRef={portalRef}
                  size={size}
                  showCandleNumbers={showCandleNumbers}
                  timeUnit={timeUnit}
                  bottomPadding={bottomPadding}
                />
              ) : null}
              <GapMarkers
                gaps={gaps}
                scaleX={scaleX}
                size={size}
                bottomPadding={bottomPadding}
              />

              <VerticalTicks
                size={size}
                bottomPadding={bottomPadding}
                scaleY={scaleY}
                numberOfTicks={10}
                tickSize={5}
                weight={1}
              />
              <HorizontalTicks
                size={size}
                bottomPadding={bottomPadding}
                scaleX={scaleX}
                numberOfTicks={10}
                tickSize={5}
                weight={1}
              />
              {tradePlanId !== 'none' ? (
                <OrdersContainer
                  symbol={symbol}
                  tradePlanId={tradePlanId}
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              <ChartCrossHairs size={size} bottomPadding={bottomPadding} />
              {tradePlanId !== 'none' ? (
                <SoftOrdersContainer
                  tradePlanId={tradePlanId}
                  symbol={symbol}
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              {tradePlanId === 'newTradePlan' ? (
                <DisplayPreTactics
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              <g ref={portalRef}></g>
            </svg>
            <VerticalLabels
              verticalZoomRef={verticalZoomRef}
              scaleY={scaleY}
              size={size}
              fontSize="10px"
              numberOfTicks={10}
              bottomPadding={bottomPadding}
            />
            {tradePlanId !== 'none' ? (
              <ChartContextMenu
                hash={hash}
                timeUnit={timeUnit}
                candleDuration={candleDuration}
                autoSend={autoSend}
                tradePlanId={tradePlanId}
                scaleY={scaleY}
              />
            ) : null}
          </MouseCoordinatesWrapper>
          ) : null}
        </div>
        {!showSkeleton && !showEmptyState && !requestError ? (
          <HorizontalLabels
            scaleX={scaleX}
            size={size}
            timeStampArray={timeStampArray}
            timeUnit={timeUnit}
          />
        ) : null}
      </div>
    );
  }
);
